import { createApi } from '@reduxjs/toolkit/query/react';
import { symfonyBaseQuery } from '@/services/SymfonyClient';
import {
  uploadCompanyResourceFile,
  UploadCompleteResponse,
} from '@/services/SymfonyFileUploadService';
import {
  Resource,
  ResourcesResponse,
} from '@/slices/resources/resources.types';

export const resourcesApi = createApi({
  baseQuery: symfonyBaseQuery({
    baseUrl: '/api/companies',
  }),
  reducerPath: 'resources',
  tagTypes: ['Resources'],
  endpoints: (build) => ({
    getResources: build.query<
      Resource[],
      {
        companyId: string;
      }
    >({
      query: ({ companyId }) => ({
        url: `/${companyId}/resources`,
        method: 'GET',
      }),
      transformResponse: (response: ResourcesResponse) =>
        response.resources ?? [],
      providesTags: (_result, _error, { companyId }) => [
        { type: 'Resources', id: companyId },
      ],
    }),
    createLinkResource: build.mutation<
      Resource,
      {
        companyId: string;
        name: string;
        url: string;
      }
    >({
      query: ({ companyId, name, url }) => ({
        url: `/${companyId}/resources`,
        method: 'POST',
        data: {
          name,
          url,
        },
      }),
      async onQueryStarted({ companyId }, { dispatch, queryFulfilled }) {
        try {
          const { data: createdResource } = await queryFulfilled;

          dispatch(
            resourcesApi.util.updateQueryData(
              'getResources',
              { companyId },
              (draft) => {
                draft.unshift(createdResource);
              },
            ),
          );
        } catch {
          // Keep cache untouched when creation fails.
        }
      },
    }),
    createFileResource: build.mutation<
      UploadCompleteResponse,
      {
        companyId: string;
        file: File;
        metadata?: Record<string, string>;
      }
    >({
      async queryFn({ companyId, file, metadata }) {
        const uploadResult = await uploadCompanyResourceFile({
          companyId,
          file,
          metadata,
        });

        if ('error' in uploadResult) {
          return { error: uploadResult.error };
        }

        return { data: uploadResult.data };
      },
      invalidatesTags: (_result, _error, { companyId }) => [
        { type: 'Resources', id: companyId },
      ],
    }),
    removeResource: build.mutation<
      void,
      {
        companyId: string;
        resourceId: string;
      }
    >({
      query: ({ companyId, resourceId }) => ({
        url: `/${companyId}/resources/${resourceId}`,
        method: 'DELETE',
      }),
      async onQueryStarted(
        { companyId, resourceId },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          resourcesApi.util.updateQueryData(
            'getResources',
            { companyId },
            (draft) => {
              const index = draft.findIndex(
                (resource) => resource.id === resourceId,
              );
              if (index >= 0) {
                draft.splice(index, 1);
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    updateResource: build.mutation<
      Resource,
      {
        companyId: string;
        resourceId: string;
        name: string;
      }
    >({
      query: ({ companyId, resourceId, name }) => ({
        url: `/${companyId}/resources/${resourceId}`,
        method: 'PATCH',
        data: {
          name,
        },
      }),
      async onQueryStarted(
        { companyId, resourceId, name },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          resourcesApi.util.updateQueryData(
            'getResources',
            { companyId },
            (draft) => {
              const resource = draft.find((item) => item.id === resourceId);
              if (resource) {
                resource.name = name;
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

export default resourcesApi;
